/* eslint-disable react-hooks/rules-of-hooks */
import { useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import {
  DateRange,
  DateRangePicker as RangePicker,
  LocalizationProvider,
  PickersShortcutsItem,
} from '@mui/x-date-pickers-pro'
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFns'
import { Colors, Shapes, Typographies, useTypography } from 'src/components/theme'
import { CalendarEvent } from '@emotion-icons/boxicons-solid/CalendarEvent'
import { isEqual, isBefore } from 'date-fns'

type DataRangeProps = {
  value?: [Date | string, Date | string]
  onChange?: (value: [Date, Date]) => void
  startLabel?: string
  endLabel?: string
  shortcuts?: PickersShortcutsItem<[Date, Date]>[]
  minDate?: Date
  maxDate?: Date
}

const Wrapper = styled.div`
  padding-top: 4px !important;

  & .MuiStack-root {
    align-items: center;
  }

  & .MuiInputBase-root {
    height: 30px;
  }

  & .MuiInputBase-root fieldset {
    border-color: ${Colors.borderInput};
    border-radius: ${Shapes.borderRadius}px;
    box-shadow: 0px 1px 2px 0px ${Colors.shadowInput};
  }
  & .MuiInputBase-root:hover fieldset {
    border-color: ${Colors.borderInputHover};
  }

  & .MuiInputBase-root svg {
    color: ${Colors.textMedium};
    flex-shrink: 0;
    width: 14px;
    height: 14px;
  }

  .MuiOutlinedInput-root {
    flex-shrink: 0;
    flex: 1;
    padding: ${Shapes.spacing[2]}px;
    background-color: ${Colors.bgWhite};
    width: 120px;
    gap: ${Shapes.spacing[1]}px;
    input {
      padding: 0;
      color: ${Colors.textBlack};
      ${useTypography(Typographies.text1)}
    }
  }

  & .MuiInputLabel-root {
    top: -10px;
  }

  & .MuiInputLabel-root.MuiFormLabel-filled {
    top: 0px;
  }
`

export const DateRangePicker = (props: DataRangeProps) => {
  const { value, minDate, maxDate } = props
  const valueDate: [Date, Date] | undefined = value ? [new Date(value[0]), new Date(value[1])] : undefined
  const [internalValue, setInternalValue] = useState<DateRange<Date> | undefined>(valueDate)
  const [open, setOpen] = useState(false)
  const onAceptJustInvoked = useRef(false)

  const onChange = (value: DateRange<Date>) => {
    setInternalValue(value)
  }

  const onAccept = (value: DateRange<Date>) => {
    if (props.onChange && value && value[0] && value[1]) {
      onAceptJustInvoked.current = true
      props.onChange([value[0], value[1]])
      setTimeout(() => {
        onAceptJustInvoked.current = false
      }, 200)
    }
    onClose()
  }

  const onOpen = () => {
    setOpen(true)
  }

  const onClose = () => {
    setOpen(false)
    checkForChanges()
  }

  const checkForChanges = () => {
    if (onAceptJustInvoked.current) {
      return
    }

    if (
      internalValue &&
      value &&
      internalValue[0] &&
      internalValue[1] &&
      value[0] &&
      value[1] &&
      (!isEqual(internalValue[0], new Date(value[0])) || !isEqual(internalValue[1], new Date(value[1]))) &&
      isBefore(internalValue[0], internalValue[1])
    ) {
      props.onChange?.([internalValue[0], internalValue[1]])
    }
  }

  const onShortcutClick = (value: DateRange<Date>) => {
    if (!props.onChange || !value || !value[0] || !value[1]) {
      return
    }
    setInternalValue(value)
    props.onChange([value[0], value[1]])
    setOpen(false)
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      onClose()
    }
  }

  useEffect(() => {
    if (!value) {
      return
    }
    const newDateValue: [Date, Date] | undefined = value ? [new Date(value[0]), new Date(value[1])] : undefined
    if (
      newDateValue &&
      (!internalValue || newDateValue[0] !== internalValue[0] || newDateValue[1] !== internalValue[1])
    ) {
      setInternalValue(newDateValue)
    }
  }, [value])

  return (
    <Wrapper>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <RangePicker
          open={open}
          onOpen={onOpen}
          onClose={onClose}
          value={internalValue}
          onChange={onChange}
          onAccept={onAccept}
          localeText={{ start: props.startLabel, end: props.endLabel }}
          slotProps={{
            textField: {
              InputProps: {
                startAdornment: <CalendarEvent />,
              },
              onKeyDown,
            },
            shortcuts: {
              items: props.shortcuts,
              onChange: onShortcutClick,
            },
          }}
          minDate={minDate}
          maxDate={maxDate}
        />
      </LocalizationProvider>
    </Wrapper>
  )
}
